import React, { useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import {
  Alert, Button, Card, CardBody, Col, Container, Form, Input, Jumbotron, Label, Row, Spinner, Table,
} from 'reactstrap';
import moment from 'moment';
import { GET_KEEP_USAGE_STATS } from '../../query/query';

const DAY = 'YYYY-MM-DD';
const BAR_COLOR = '#2a78d6';
const CHART_HEIGHT = 120;

const StatTile = ({ label, value, detail }) => (
  <Col sm="6" md="3" className="mb-3">
    <Card>
      <CardBody>
        <div className="text-muted small">{label}</div>
        <div style={{ fontSize: 28, fontWeight: 600 }}>{value}</div>
        {detail && <div className="text-muted small">{detail}</div>}
      </CardBody>
    </Card>
  </Col>
);

// One measure per chart: bars per day, hover a bar for its day and count.
const DailyBars = ({ title, days, field }) => {
  const max = Math.max(1, ...days.map((day) => day[field]));
  const total = days.reduce((sum, day) => sum + day[field], 0);
  const barWidth = 100 / days.length;
  return (
    <div className="mb-4">
      <h5>
        {title}
        <small className="text-muted">{`  ${total} in the window · busiest day ${max}`}</small>
      </h5>
      <svg
        width="100%"
        height={CHART_HEIGHT}
        viewBox={`0 0 100 ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${title}: ${total} in the window`}
        style={{ borderBottom: '1px solid #ccc' }}
      >
        {days.map((day, i) => {
          const height = (day[field] / max) * (CHART_HEIGHT - 4);
          return (
            <g key={day.date}>
              <rect x={i * barWidth} y={0} width={barWidth} height={CHART_HEIGHT} fill="transparent">
                <title>{`${day.date}: ${day[field]}`}</title>
              </rect>
              {day[field] > 0 && (
                <rect
                  x={i * barWidth + barWidth * 0.15}
                  y={CHART_HEIGHT - height}
                  width={barWidth * 0.7}
                  height={height}
                  fill={BAR_COLOR}
                  pointerEvents="none"
                />
              )}
            </g>
          );
        })}
      </svg>
      <div className="d-flex justify-content-between text-muted small">
        <span>{days[0] && days[0].date}</span>
        <span>{days.length > 0 && days[days.length - 1].date}</span>
      </div>
    </div>
  );
};

const KeepDashboardPage = () => {
  const [range, setRange] = useState({
    since: moment.utc().subtract(90, 'days').format(DAY),
    until: moment.utc().format(DAY),
  });
  const [form, setForm] = useState(range);
  const { data, loading, error } = useQuery(GET_KEEP_USAGE_STATS, {
    variables: range,
    fetchPolicy: 'network-only',
  });
  const stats = data && data.savGetKeepUsageStats;

  const onSubmit = (e) => {
    e.preventDefault();
    setRange(form);
  };

  return (
    <Container>
      <Jumbotron>
        <h3>KEEP dashboard</h3>
        <hr className="my-2" />
        <Form onSubmit={onSubmit} className="mb-3">
          <Row form>
            <Col sm="4">
              <Label for="keep-since">From (UTC)</Label>
              <Input
                type="date"
                id="keep-since"
                value={form.since}
                onChange={({ target }) => setForm({ ...form, since: target.value })}
              />
            </Col>
            <Col sm="4">
              <Label for="keep-until">To (UTC, included)</Label>
              <Input
                type="date"
                id="keep-until"
                value={form.until}
                onChange={({ target }) => setForm({ ...form, until: target.value })}
              />
            </Col>
            <Col sm="4" className="d-flex align-items-end">
              <Button color="primary" type="submit">Show</Button>
            </Col>
          </Row>
        </Form>

        {loading && <Spinner color="primary" />}
        {error && <Alert color="danger">{error.message}</Alert>}
        {stats && (
          <div>
            <h5>All time</h5>
            <Row>
              <StatTile label="KEEPs" value={stats.totalLinks} />
              <StatTile label="KEEPs with an owner" value={stats.linksWithOwner} />
              <StatTile label="KEEPs with a message put on them" value={stats.linksWithSyncedMessage} />
              <StatTile
                label="Messages put on a KEEP"
                value={`${stats.syncedMessagePercent}%`}
                detail={`${stats.syncedMessages} of ${stats.totalMessages} messages`}
              />
            </Row>
            <Row>
              <StatTile label="Registered by the factory" value={stats.linksFactoryRegistered} />
              <StatTile
                label="Not registered by the factory"
                value={stats.linksPairedWithoutFactoryRegistration}
                detail="Created when an owner or a sender first paired them"
              />
              <StatTile
                label="Screen type corrected by the app"
                value={stats.screenTypeHealedCount}
                detail={'Registered with the wrong 2.13" B&W panel model, fixed during a sync'}
              />
            </Row>

            <DailyBars title="New setups per day (first owner paired)" days={stats.days} field="newSetups" />
            <DailyBars title="First syncs per day (first message put on the KEEP)" days={stats.days} field="firstSyncs" />

            <details>
              <summary>Table</summary>
              <Table size="sm" bordered className="mt-2">
                <thead>
                  <tr>
                    <th>Day (UTC)</th>
                    <th>New setups</th>
                    <th>First syncs</th>
                  </tr>
                </thead>
                <tbody>
                  {[...stats.days].reverse().map((day) => (
                    <tr key={day.date}>
                      <td>{day.date}</td>
                      <td>{day.newSetups}</td>
                      <td>{day.firstSyncs}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </details>
          </div>
        )}
      </Jumbotron>
    </Container>
  );
};

export default KeepDashboardPage;



// WEBPACK FOOTER //
// ./src/components/keep-info/keep-dashboard.js